import kitePhoto from '../assets/dfc-schools/KITE BEACH FV.jpg';
import warqaPhoto from '../assets/dfc-schools/AL WARQA FV.jpg';
import zabeelPhoto from '../assets/dfc-schools/ZAABEEL PARK FV.jpg';
import kiteLockupLight from '../assets/dfc-village/logo-village-kite-light.png';
import kiteLockupDark from '../assets/dfc-village/logo-village-kite-dark.png';
import warqaLockupLight from '../assets/dfc-village/logo-village-warqa-light.png';
import warqaLockupDark from '../assets/dfc-village/logo-village-warqa-dark.png';
import zabeelLockupLight from '../assets/dfc-village/logo-village-zabeel-light.png';
import zabeelLockupDark from '../assets/dfc-village/logo-village-zabeel-dark.png';
// Kite's partner lockup, normalised to the other two (DFC's file fills more of the same canvas,
// and its dark pill text was black where the others' is white). The originals stay in the
// folder unused; swap these back for a corrected file from DFC when one arrives.
import kitePartnerLight from '../assets/dfc-village/logo-village-kite-partner-light-v2.png';
import kitePartnerDark from '../assets/dfc-village/logo-village-kite-partner-dark-v2.png';
import warqaPartnerLight from '../assets/dfc-village/logo-village-warqa-partner-light.png';
import warqaPartnerDark from '../assets/dfc-village/logo-village-warqa-partner-dark.png';
import zabeelPartnerLight from '../assets/dfc-village/logo-village-zabeel-partner-light.png';
import zabeelPartnerDark from '../assets/dfc-village/logo-village-zabeel-partner-dark.png';
import { DfcLocale } from './dfcLocale';

/**
 * Static metadata for the three DFC 30x30 Fitness Villages.
 *
 * This is the single record of village identity (key, display name, photo) for both the
 * public Fitness Village journey and the schools programme — `schoolVillages.ts` imports
 * the shared fields from here so the two surfaces can never disagree on a village's name
 * or artwork. Programme-specific fields (the schools' visiting days, age groups) stay in
 * their own module.
 *
 * `officialPrefix` and `hours` are DFC's official village names and opening hours (26/09
 * feedback).
 */

/** Kite Beach, Al Warqa'a, Zabeel — the order the client wants them shown, not alphabetical. */
export const FITNESS_VILLAGE_DISPLAY_ORDER = ['kite', 'warqa', 'zabeel'] as const;

/** 'kite' | 'warqa' | 'zabeel' — derived so it can't drift from the display order. */
export type FitnessVillageKey = (typeof FITNESS_VILLAGE_DISPLAY_ORDER)[number];

/**
 * The challenge window, inclusive. The village page's day strip never shows a day outside it,
 * whatever the company's `bookingRange` allows.
 */
export const FITNESS_VILLAGE_CHALLENGE_WINDOW = { start: '2026-10-31', end: '2026-11-29' } as const;

/** The words a visitor reads about a village — English on the record itself, Arabic in `ar`. */
export interface FitnessVillageText {
  displayName: string;
  /** "DP World 30x30 Fitness Village" — the official name, less `displayName` */
  officialPrefix: string;
  /** One line per schedule: ["Weekdays 15:00 – 23:00", "Weekends 07:00 – 23:00"] */
  hours: string[];
  /**
   * Court booking hours, same shape as `hours` — DFC 12/10: "add the timings for courts
   * bookings, they are all already approved". The Courts tab shows them in place of the
   * opening hours; until they are set here it keeps the opening hours.
   */
  courtHours?: string[];
}

export interface FitnessVillageMeta extends FitnessVillageText {
  key: FitnessVillageKey;
  /**
   * The Arabic village design's text. `displayName` above stays English whatever the language:
   * it is also matched against backend service names (`stripVillageName`).
   * UNVERIFIED — DFC's official Arabic names to confirm.
   */
  ar: FitnessVillageText;
  /** Bundled photo; used when the service image is missing or fails to load */
  localPhoto: string;
  /**
   * Village lockup artwork, one per light / dark mode (DFC, 08/10). Each is drawn on a solid
   * plate — white for `light`, black for `dark` — so it must sit on that plate. A mode
   * without its own artwork falls back to the other one (see `getVillageLockup`).
   */
  lockups: Partial<Record<VillageLockupMode, string>>;
  /**
   * The lockup with the village's partner beside it (DP World / Dubai Municipality), same
   * light / dark rules. The home's lockup band shows these — DFC 12/10, "PARTNER Village
   * LOCKUP" — but only once every village has one, so the band never mixes the two styles.
   */
  partnerLockups: Partial<Record<VillageLockupMode, string>>;
}

export type VillageLockupMode = 'light' | 'dark';

export interface VillageLockup {
  src: string;
  /** The plate the artwork was drawn on — not always the page's mode, after a fallback */
  plate: VillageLockupMode;
}

export const FITNESS_VILLAGE_META: Record<FitnessVillageKey, FitnessVillageMeta> = {
  kite: {
    key: 'kite',
    displayName: 'Kite Beach',
    officialPrefix: 'DP World 30x30 Fitness Village',
    hours: ['Weekdays 15:00 – 23:00', 'Weekends 07:00 – 23:00'],
    ar: {
      displayName: 'شاطئ كايت',
      officialPrefix: 'قرية دي بي ورلد للياقة 30×30',
      hours: ['أيام الأسبوع 15:00 – 23:00', 'عطلة نهاية الأسبوع 07:00 – 23:00'],
    },
    localPhoto: kitePhoto,
    lockups: { light: kiteLockupLight, dark: kiteLockupDark },
    partnerLockups: { light: kitePartnerLight, dark: kitePartnerDark },
  },
  warqa: {
    key: 'warqa',
    displayName: "Al Warqa'a Park",
    officialPrefix: 'Dubai Municipality 30x30 Fitness Village',
    hours: ['Daily 16:00 – 23:00'],
    ar: {
      displayName: 'حديقة الورقاء',
      officialPrefix: 'قرية بلدية دبي للياقة 30×30',
      hours: ['يومياً 16:00 – 23:00'],
    },
    localPhoto: warqaPhoto,
    lockups: { light: warqaLockupLight, dark: warqaLockupDark },
    partnerLockups: { light: warqaPartnerLight, dark: warqaPartnerDark },
  },
  zabeel: {
    key: 'zabeel',
    displayName: 'Zabeel Park',
    officialPrefix: 'Dubai Municipality 30x30 Fitness Village',
    hours: ['Weekdays 16:00 – 23:00', 'Weekends 12:00 – 23:00'],
    ar: {
      displayName: 'حديقة زعبيل',
      officialPrefix: 'قرية بلدية دبي للياقة 30×30',
      hours: ['أيام الأسبوع 16:00 – 23:00', 'عطلة نهاية الأسبوع 12:00 – 23:00'],
    },
    localPhoto: zabeelPhoto,
    lockups: { light: zabeelLockupLight, dark: zabeelLockupDark },
    partnerLockups: { light: zabeelPartnerLight, dark: zabeelPartnerDark },
  },
};

/** A village's name, official name and hours in the visitor's language. */
export const getFitnessVillageText = (
  meta: FitnessVillageMeta,
  locale: DfcLocale
): FitnessVillageText => (locale === 'ar' ? meta.ar : meta);

/**
 * The lockup for the page's light / dark mode, or — when that mode has no artwork — the
 * other mode's, still on its own plate. `undefined` when the village has none at all.
 */
const pickLockup = (
  lockups: Partial<Record<VillageLockupMode, string>>,
  isDark: boolean
): VillageLockup | undefined => {
  const preferred: VillageLockupMode = isDark ? 'dark' : 'light';
  const fallback: VillageLockupMode = isDark ? 'light' : 'dark';
  const plate = lockups[preferred] ? preferred : lockups[fallback] ? fallback : undefined;
  return plate ? { src: lockups[plate] as string, plate } : undefined;
};

export const getVillageLockup = (
  meta: FitnessVillageMeta,
  isDark: boolean
): VillageLockup | undefined => pickLockup(meta.lockups, isDark);

/** The partner lockup for the page's mode — the same fallback as `getVillageLockup`. */
export const getVillagePartnerLockup = (
  meta: FitnessVillageMeta,
  isDark: boolean
): VillageLockup | undefined => pickLockup(meta.partnerLockups, isDark);

/** Villages are backend `Service`s; the key is recovered from the service name, like schools. */
export const getFitnessVillageKey = (serviceName?: string): FitnessVillageKey | undefined => {
  const normalizedName = (serviceName || '').toLowerCase();
  return FITNESS_VILLAGE_DISPLAY_ORDER.find(keyword => normalizedName.includes(keyword));
};

export const getFitnessVillageMeta = (serviceName?: string): FitnessVillageMeta | undefined => {
  const key = getFitnessVillageKey(serviceName);
  return key ? FITNESS_VILLAGE_META[key] : undefined;
};

const getFitnessVillageOrderIndex = (serviceName?: string) => {
  const key = getFitnessVillageKey(serviceName);
  return key ? FITNESS_VILLAGE_DISPLAY_ORDER.indexOf(key) : FITNESS_VILLAGE_DISPLAY_ORDER.length;
};

/** Display order first, then alphabetical for anything unrecognised. */
export const compareFitnessVillagesByDisplayOrder = (leftName?: string, rightName?: string) => {
  const orderDiff = getFitnessVillageOrderIndex(leftName) - getFitnessVillageOrderIndex(rightName);
  if (orderDiff !== 0) return orderDiff;
  return (leftName || '').localeCompare(rightName || '');
};
